// src/redux/slices/trashSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchDeletedDocuments,
  moveDocumentToTrash,
  restoreDocument,
  permanentlyDeleteDocument,
  bulkRestoreDocuments,
  bulkDeleteDocuments,
  emptyTrash,
} from "../thunks/trashThunks";

const initialState = {
  items: [],
  total: 0,
  totalSize: 0,

  loading: false,
  error: null,
  selectedRowKeys: [],

  searchText: "",
  sortOrder: "desc",
  filterLocation: "all",

  isRestoring: false,
  isDeleting: false,
  isEmptying: false,
  isMovingToTrash: false,
};

const removeById = (items, id) =>
  (items || []).filter((it) => (it.trashId || it.key || it._id || it.id) !== id);

const trashSlice = createSlice({
  name: "trash",
  initialState,
  reducers: {
    setSelectedRowKeys: (state, action) => {
      state.selectedRowKeys = action.payload;
    },
    clearSelection: (state) => {
      state.selectedRowKeys = [];
    },
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setFilterLocation: (state, action) => {
      state.filterLocation = action.payload;
    },
    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === "asc" ? "desc" : "asc";
    },
    resetTrashState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // ========== MOVE TO TRASH ==========
      .addCase(moveDocumentToTrash.pending, (state) => {
        state.isMovingToTrash = true;
        state.error = null;
      })
      .addCase(moveDocumentToTrash.fulfilled, (state) => {
        state.isMovingToTrash = false;
      })
      .addCase(moveDocumentToTrash.rejected, (state, action) => {
        state.isMovingToTrash = false;
        state.error = action.payload;
      })

      // ========== FETCH DELETED DOCUMENTS ==========
      .addCase(fetchDeletedDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeletedDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.total = action.payload.total ?? state.items.length;
        state.totalSize = action.payload.totalSize ?? 0;
      })
      .addCase(fetchDeletedDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ========== RESTORE DOCUMENT ==========
      .addCase(restoreDocument.pending, (state) => {
        state.isRestoring = true;
        state.error = null;
      })
      .addCase(restoreDocument.fulfilled, (state, action) => {
        state.isRestoring = false;

        const id = action.payload?.trashId || action.payload?.key;
        state.items = removeById(state.items, id);
        state.total = Math.max(0, (state.total || 0) - 1);
        state.selectedRowKeys = state.selectedRowKeys.filter((k) => k !== id);
      })
      .addCase(restoreDocument.rejected, (state, action) => {
        state.isRestoring = false;
        state.error = action.payload;
      })

      // ========== PERMANENTLY DELETE DOCUMENT ==========
      .addCase(permanentlyDeleteDocument.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(permanentlyDeleteDocument.fulfilled, (state, action) => {
        state.isDeleting = false;

        const id = action.payload?.trashId || action.payload?.key;
        state.items = removeById(state.items, id);
        state.total = Math.max(0, (state.total || 0) - 1);
        state.selectedRowKeys = state.selectedRowKeys.filter((k) => k !== id);
      })
      .addCase(permanentlyDeleteDocument.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // ========== BULK RESTORE ==========
      .addCase(bulkRestoreDocuments.pending, (state) => {
        state.isRestoring = true;
        state.error = null;
      })
      .addCase(bulkRestoreDocuments.fulfilled, (state, action) => {
        state.isRestoring = false;

        const restoredKeys = action.payload?.restoredKeys || [];
        state.items = (state.items || []).filter(
          (it) => !restoredKeys.includes(it.trashId || it.key)
        );
        state.total = Math.max(0, (state.total || 0) - restoredKeys.length);
        state.selectedRowKeys = [];
      })
      .addCase(bulkRestoreDocuments.rejected, (state, action) => {
        state.isRestoring = false;
        state.error = action.payload;
      })

      // ========== BULK DELETE ==========
      .addCase(bulkDeleteDocuments.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(bulkDeleteDocuments.fulfilled, (state, action) => {
        state.isDeleting = false;

        const deletedKeys = action.payload?.deletedKeys || [];
        state.items = (state.items || []).filter(
          (it) => !deletedKeys.includes(it.trashId || it.key)
        );
        state.total = Math.max(0, (state.total || 0) - deletedKeys.length);
        state.selectedRowKeys = [];
      })
      .addCase(bulkDeleteDocuments.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // ========== EMPTY TRASH ==========
      .addCase(emptyTrash.pending, (state) => {
        state.isEmptying = true;
        state.error = null;
      })
      .addCase(emptyTrash.fulfilled, (state) => {
        state.isEmptying = false;
        state.items = [];
        state.total = 0;
        state.totalSize = 0;
        state.selectedRowKeys = [];
      })
      .addCase(emptyTrash.rejected, (state, action) => {
        state.isEmptying = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedRowKeys,
  clearSelection,
  setSearchText,
  setSortOrder,
  setFilterLocation,
  toggleSortOrder,
  resetTrashState,
} = trashSlice.actions;

export default trashSlice.reducer;
